import React, { useContext } from 'react'
import { Col, Row, Container } from 'react-grid-system'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import { ErrorBoundary } from 'react-error-boundary'

import { ServiceCard } from 'pyrexx-react-library'
import StatusLightWithLabel from '../Status/StatusLightWithLabel'
import WithSkeleton from './WithSkeleton'
import SimpleLink from '../Simple/SimpleLink'
import QueryRendererContainer from '../QueryRendererContainer'
import NotAvailableContent from './NotAvailableContent'

import UserContext from '../../helper/userContext'
import graphql from 'babel-plugin-relay/macro'
import { Routes } from '../../configs/RouteConfig'

import serviceImg from '../../assets/overview/service.png'
import serviceInactiveImg from '../../assets/overview/service-inactive.png'
import serviceContentImg from '../../assets/overview/service-content.png'

const StyledCol = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
`

const CardItemTitle = styled.p`
  margin-top: 0;
  text-align: center;
`

const ContractsCardContent = (props) => {
  const { data = {}, loading = false } = props
  const { t } = useTranslation()

  return (
    <Container style={{ width: '100%' }}>
      <Row>
        <StyledCol>
          <WithSkeleton variant='md' loading={loading}>
            <CardItemTitle>{t('COMPLETED')}</CardItemTitle>
          </WithSkeleton>
        </StyledCol>

        <StyledCol>
          <WithSkeleton variant='md' loading={loading}>
            <CardItemTitle>{t('PLANNED')}</CardItemTitle>
          </WithSkeleton>
        </StyledCol>

        <StyledCol>
          <WithSkeleton variant='md' loading={loading}>
            <CardItemTitle>{t('INCOMPLETE')}</CardItemTitle>
          </WithSkeleton>
        </StyledCol>
      </Row>

      <Row>
        <StyledCol>
          <WithSkeleton loading={loading}>
            <Row justify='center'>
              <StatusLightWithLabel status='success' label={data?.completed} />
            </Row>
          </WithSkeleton>
        </StyledCol>

        <StyledCol>
          <WithSkeleton loading={loading}>
            <Row justify='center'>
              <StatusLightWithLabel status='warning' label={data?.planned} />
            </Row>
          </WithSkeleton>
        </StyledCol>

        <StyledCol>
          <WithSkeleton loading={loading}>
            <Row justify='center'>
              <StatusLightWithLabel status='error' label={data?.incompleted} />
            </Row>
          </WithSkeleton>
        </StyledCol>
      </Row>
    </Container>
  )
}

const ContractsCard = ({ data = {}, loading = false, error = false }) => {
  const { currentLanguage } = useContext(UserContext)
  const { t } = useTranslation()

  if (error) {
    return (
      <ServiceCard
        imgSrc={serviceInactiveImg}
        title={t('CONTRACTED SERVICES')}
        content={<NotAvailableContent imgSrc={serviceContentImg} />}
        isDisabled
      />
    )
  }

  return (
    <ServiceCard
      imgSrc={serviceImg}
      title={t('CONTRACTED SERVICES')}
      content={<ContractsCardContent data={data} loading={loading} />}
      link={
        !loading && (
          <SimpleLink
            to={'/' + currentLanguage + Routes.contracts}
            font='quicksand'
          >
            {t('ALL') + ' >'}
          </SimpleLink>
        )
      }
    />
  )
}

const query = graphql`
  query ContractsCardQuery($companyId: ID!) {
    Me {
      contractsStatus: Company(id: $companyId) {
        completed: countContracts(
          options: {
            filters: [
              {
                name: "status"
                filterType: text
                operator: AND
                conditions: [
                  {
                    value: [
                      "DONE_BUT_NOT_FINAL_BILLED"
                      "FINAL_BILLED_NOT_EXPIRED_NO_MAINTENANCE"
                    ]
                    type: equals
                  }
                ]
              }
            ]
          }
        )
        planned: countContracts(
          options: {
            filters: [
              {
                name: "status"
                filterType: text
                operator: AND
                conditions: [
                  { value: ["ACTIVE", "UNDER_CONSTRUCTION"], type: equals }
                ]
              }
            ]
          }
        )
        incompleted: countContracts(
          options: {
            filters: [
              {
                name: "status"
                filterType: text
                operator: AND
                conditions: [
                  {
                    value: [
                      "DISCARDED"
                      "READY_FOR_ACTIVATION"
                      "TEMP_SUSPENDED"
                      "EXPIRED_AND_FINAL_BILLED"
                      "MERGED_INTO_OTHER_CONTRACT"
                    ]
                    type: equals
                  }
                ]
              }
            ]
          }
        )
      }
    }
  }
`

export default ({ isHidden = true }) => {
  const { user } = useContext(UserContext)

  const variables = {
    companyId: user.companyId,
  }

  if (isHidden) {
    return null
  }

  return (
    <ErrorBoundary fallbackRender={() => null}>
      <QueryRendererContainer
        query={query}
        variables={variables}
        loading={<ContractsCard loading />}
        render={(data) => {
          const contractsStatus = data?.Me?.contractsStatus

          return <ContractsCard data={contractsStatus} />
        }}
      />
    </ErrorBoundary>
  )
}
